/**
 * Black Marble — INTRO CINEMATIC controller (plain-DOM, NO React). A faithful
 * re-port of the authoritative Claude Design reference: a 1920×1080 design stage
 * scaled to cover the viewport so reference proportions are preserved, holding a
 * stack of layers driven per-frame from the pure introTimeline.ts schedule —
 * a 190-star twinkling field, a cold-sun ignition with a raking sliver, the
 * Three.js obsidian marble (introMarble.ts), a 120-name perspective grid over a
 * stone ground with a sweep band, 38 drifting motes, and the carved title
 * landing (glyph-by-glyph BLACK MARBLE, the typewriter sub-line, the carved-
 * souls label and the pulsing [ ENTER ] BEGIN), all under a vignette+grain film
 * and a retracting letterbox.
 *
 * `play()` runs a requestAnimationFrame clock 0 → 30s and resolves on dismiss
 * (click / Enter / Space / Escape) OR when the clock reaches 30, handing off
 * seamlessly to the live TitleScreen. `dispose()` tears everything down. The
 * accessibility reduce-motion flag collapses straight to the static title beat.
 */
import './introCinematic.css';
import { createPlateLayer } from './plateLayer.js';
import { IntroScene } from './introScene.js';
import { CineClock } from './cineClock.js';
import { getSettings } from '../settings/store.js';
import {
  INTRO_DURATION_SEC,
  NAME_POOL,
  NAME_SEED,
  beatOpacity,
  clamp,
  easeOutCubic,
  formatNamesCarved,
  interpolate,
  introPointCloud,
  letterboxBarHeight,
  moteState,
  starfieldDrift,
  twinkleAlpha,
} from './introTimeline.js';

const STAR_COUNT = 190;
const MOTE_COUNT = 38;
const STAR_SEED = 0x9e3779b9;
const MOTE_SEED = 0x53a7c1d3;
const TITLE_TEXT = 'BLACK MARBLE';
const SUBTITLE = 'A RECORD OF THE LOST';
const ENTER_TEXT = '[ ENTER ] BEGIN';
const TITLE_START = 23.4;
// Reference parity: sub-line starts AFTER the last glyph lands (~25.35) and
// types at the original cadence (0.045 s/char → done ~26.5).
const SUB_START = 25.6;
const SUB_CHAR_SEC = 0.045;
/** Reference glyph carve: 0.135 s stagger per glyph, 0.6 s carve each. */
const GLYPH_STEP = 0.135;
const GLYPH_DUR = 0.6;

interface StarHandle {
  el: HTMLElement;
  base: number;
  phase: number;
  speed: number;
}

interface MoteHandle {
  el: HTMLElement;
  x: number;
  y: number;
  z: number;
}

export class IntroCinematic {
  private readonly root: HTMLDivElement;
  private readonly stage: HTMLDivElement;
  private readonly engineScene = new IntroScene();
  private readonly clock = new CineClock();
  private readonly stars: StarHandle[] = [];
  private readonly motes: MoteHandle[] = [];
  private readonly glyphs: HTMLElement[] = [];
  private starfield!: HTMLElement;
  private ignition!: HTMLElement;
  private sliver!: HTMLElement;
  private namesGroup!: HTMLElement;
  private sweep!: HTMLElement;
  private dust!: HTMLElement;
  private titleGroup!: HTMLElement;
  private titleMark!: HTMLElement;
  private titleRake!: HTMLElement;
  private subLine!: HTMLElement;
  private enter!: HTMLElement;
  private carvedLabel!: HTMLElement;
  private letterbox!: HTMLElement;
  private raf = 0;
  private underlayRaf = 0;
  private playing = false;
  private resolve: (() => void) | null = null;
  /** Per-frame clock listener (Wave 1: IntroAudio.sync — cues lock to the film). */
  tickListener: ((t: number) => void) | null = null;
  private readonly onKey = (event: KeyboardEvent): void => this.onKeydown(event);
  private readonly onResize = (): void => this.applyScale();

  constructor() {
    this.root = document.createElement('div');
    this.root.id = 'intro-cinematic';
    this.root.style.display = 'none';
    this.stage = document.createElement('div');
    this.stage.className = 'ic-stage';
    this.root.appendChild(this.stage);
    this.buildLayers();
    document.body.appendChild(this.root);
    this.root.addEventListener('click', () => this.dismiss());
  }

  /** Run the cinematic; resolves once dismissed or at t = 30. */
  play(): Promise<void> {
    return new Promise<void>((resolve) => {
      this.resolve = resolve;
      this.playing = true;
      this.root.style.display = 'block';
      this.applyScale();
      window.addEventListener('keydown', this.onKey);
      window.addEventListener('resize', this.onResize);
      const reduced = getSettings().accessibility.reduceMotion;
      this.root.classList.toggle('reduced', reduced);
      if (reduced) {
        // Reduced motion: snap the engine to the KF7 resting plate and hold it static;
        // the DOM title carve still plays (typography, not motion).
        this.engineScene.start(1920, 1080);
        this.engineScene.snapToHandoff();
        this.renderStatic();
        // Audio still runs the fixed 30s timeline under the static plate — a
        // minimal clock loop feeds the tick listener without rendering motion.
        this.clock.reset();
        const audioOnly = (ts: number): void => {
          if (!this.playing) return;
          this.clock.tick(ts);
          this.tickListener?.(clamp(this.clock.t, 0, INTRO_DURATION_SEC));
          if (this.clock.t >= INTRO_DURATION_SEC) {
            this.dismiss();
            return;
          }
          this.raf = requestAnimationFrame(audioOnly);
        };
        this.raf = requestAnimationFrame(audioOnly);
        return;
      }
      this.engineScene.start(1920, 1080);
      // Dev/QA: `?introt=N` freezes the cinematic on beat N (seconds) and re-renders it
      // every frame so a single beat can be inspected/compared against the reference.
      const frozen = new URLSearchParams(location.search).get('introt');
      if (frozen !== null) {
        const ft = clamp(parseFloat(frozen) || 0, 0, INTRO_DURATION_SEC);
        const hold = (): void => {
          this.engineScene.update(ft);
          this.render(ft);
          this.raf = requestAnimationFrame(hold);
        };
        hold();
        return;
      }
      this.clock.reset();
      this.raf = requestAnimationFrame((ts) => this.tick(ts));
    });
  }

  dispose(): void {
    if (this.underlayRaf) {
      cancelAnimationFrame(this.underlayRaf);
      this.underlayRaf = 0;
      this.engineScene.element.remove();
      this.engineScene.dispose();
      return;
    }
    if (this.playing) {
      this.dismiss();
      return;
    }
    this.teardown();
  }

  /**
   * Wave 1 handoff parity: after play() resolves, keep the ENGINE frame alive
   * under the TitleScreen — the marble keeps rotating, grain keeps breathing —
   * instead of destroying it for a static PNG plate. The DOM cinematic layers
   * are torn down; only the WebGL canvas survives, reparented as a full-screen
   * underlay. dispose() ends it once the player commits a boot action.
   */
  beginTitleUnderlay(): void {
    if (!this.engineScene.enabled) {
      this.teardown();
      return;
    }
    window.removeEventListener('keydown', this.onKey);
    window.removeEventListener('resize', this.onResize);
    const el = this.engineScene.element;
    el.classList.remove('ic-marble-canvas', 'ic-scene-canvas');
    el.classList.add('ic-underlay');
    document.body.appendChild(el);
    this.root.remove();
    const loop = (ts: number): void => {
      // Clock keeps ticking past 30: the camera sampler clamps at KF7, so only
      // the body rotation + grain advance — a live resting plate, never a pop.
      this.clock.tick(ts);
      this.engineScene.update(this.clock.t);
      this.underlayRaf = requestAnimationFrame(loop);
    };
    this.underlayRaf = requestAnimationFrame(loop);
  }

  private tick(ts: number): void {
    if (!this.playing) return;
    // Single steady rAF: one CineClock.tick() at the top, one render at the bottom.
    // The dt-clamp inside CineClock means a GLB decode hitch PAUSES cinematic time
    // rather than teleporting it — the root-cause smoothness fix.
    this.clock.tick(ts);
    const t = clamp(this.clock.t, 0, INTRO_DURATION_SEC);
    this.tickListener?.(t);
    this.engineScene.update(t);
    this.render(t);
    if (this.clock.t >= INTRO_DURATION_SEC) {
      this.dismiss();
      return;
    }
    this.raf = requestAnimationFrame((next) => this.tick(next));
  }

  private render(t: number): void {
    // When the in-engine path is live, the WebGL scene OWNS the starfield, the real
    // cenotaph (its carved inscriptions ARE the names), and the foreground ash motes —
    // so the redundant DOM backdrop layers are suppressed and only the proven DOM
    // typography + letterbox composite on top. Without WebGL (jsdom/headless) the DOM
    // backdrop remains as the visual fallback so the cinematic still plays.
    const bg = this.engineScene.enabled ? 0 : 1;
    this.renderStarfield(t, bg);
    this.ignition.style.opacity = (beatOpacity('ignition', t) * bg).toFixed(3);
    this.sliver.style.transform = `translateX(${interpolate([0.8, 4], [-62, 62], t).toFixed(1)}%) rotate(-18deg)`;
    this.renderNames(t, bg);
    this.renderDust(t, bg);
    this.renderTitle(t);
    // Reference: the NAMES CARVED label belongs to the TITLE LANDING (25.0→26.2,
    // max 0.82) — not the Names beat 15 s earlier.
    this.carvedLabel.style.opacity = (0.82 * easeOutCubic(clamp((t - 25.0) / 1.2, 0, 1))).toFixed(3);
    this.letterbox.style.setProperty('--ic-bar', `${letterboxBarHeight(t).toFixed(1)}px`);
  }

  private renderStarfield(t: number, bg: number): void {
    const fade = easeOutCubic(clamp(t / 2, 0, 1));
    this.starfield.style.transform = `translateY(${starfieldDrift(t).toFixed(2)}%)`;
    for (const s of this.stars) {
      s.el.style.opacity = (twinkleAlpha(s.base, s.phase, s.speed, t) * fade * bg).toFixed(3);
    }
  }

  private renderNames(t: number, bg: number): void {
    this.namesGroup.style.opacity = (beatOpacity('namesWall', t) * bg).toFixed(3);
    this.sweep.style.transform = `translateX(${interpolate([9.5, 15], [-120, 120], t).toFixed(1)}%)`;
  }

  private renderDust(t: number, bg: number): void {
    this.dust.style.opacity = (beatOpacity('dust', t) * bg).toFixed(3);
    for (const m of this.motes) {
      const s = moteState({ x: m.x, y: m.y, z: m.z }, t);
      m.el.style.left = `${(s.x * 100).toFixed(2)}%`;
      m.el.style.top = `${(s.y * 100).toFixed(2)}%`;
      m.el.style.opacity = s.opacity.toFixed(3);
    }
  }

  private renderTitle(t: number): void {
    this.titleGroup.style.opacity = beatOpacity('titleLanding', t).toFixed(3);
    this.glyphs.forEach((g, i) => {
      const e = easeOutCubic(clamp((t - TITLE_START - i * GLYPH_STEP) / GLYPH_DUR, 0, 1));
      g.style.opacity = e.toFixed(3);
      // Reference glyph rise: each glyph settles up 9px as it carves in.
      g.style.transform = `translateY(${((1 - e) * 9).toFixed(2)}px)`;
    });
    const shown = clamp(Math.floor((t - SUB_START) / SUB_CHAR_SEC), 0, SUBTITLE.length);
    this.subLine.textContent = SUBTITLE.slice(0, shown);
    // Reference ENTER prompt: fades 26.6→27.4 then breathes on the clock
    // (deterministic — no CSS animation drift).
    const enterIn = easeOutCubic(clamp((t - 26.6) / 0.8, 0, 1));
    const pulse = 0.55 + 0.45 * Math.sin((t - 26.6) * 2.6);
    this.enter.style.opacity = (enterIn * pulse).toFixed(3);
    // Reference title rake: a cold band sweeps the carved mark 24.8→27.4.
    this.titleRake.style.transform = `translateX(${interpolate([24.8, 27.4], [-42, 42], t).toFixed(1)}%)`;
    this.titleRake.style.opacity = interpolate(
      [24.8, 25.6, 27.0, 27.4],
      [0, 0.7, 0.5, 0],
      t,
    ).toFixed(3);
  }

  private renderStatic(): void {
    this.starfield.style.opacity = '0';
    this.ignition.style.opacity = '0';
    this.namesGroup.style.opacity = '0';
    this.dust.style.opacity = '0';
    this.titleGroup.style.opacity = '1';
    for (const g of this.glyphs) {
      g.style.opacity = '1';
      g.style.transform = 'translateY(0px)';
    }
    this.titleRake.style.opacity = '0';
    this.subLine.textContent = SUBTITLE;
    this.enter.style.opacity = '1';
    this.carvedLabel.style.opacity = '0.82';
    this.letterbox.style.setProperty('--ic-bar', '0px');
  }

  private applyScale(): void {
    const scale = Math.max(window.innerWidth / 1920, window.innerHeight / 1080);
    this.stage.style.transform = `translate(-50%, -50%) scale(${scale.toFixed(4)})`;
  }

  private dismiss(): void {
    if (!this.playing) return;
    this.playing = false;
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = 0;
    const cb = this.resolve;
    this.resolve = null;
    // NO teardown here (Wave 1 handoff): the engine scene must survive so
    // beginTitleUnderlay() can keep the live frame under the TitleScreen.
    // Full teardown happens in beginTitleUnderlay's no-WebGL branch or dispose().
    cb?.();
  }

  private teardown(): void {
    window.removeEventListener('keydown', this.onKey);
    window.removeEventListener('resize', this.onResize);
    this.engineScene.dispose();
    this.root.remove();
  }

  private onKeydown(event: KeyboardEvent): void {
    if (!this.playing) return;
    if (event.key === 'Enter' || event.key === ' ' || event.key === 'Escape') {
      event.preventDefault();
      this.dismiss();
    }
  }

  private buildLayers(): void {
    this.starfield = this.appendLayer('ic-starfield');
    this.populateStars();
    this.ignition = this.appendLayer('ic-ignition');
    this.sliver = document.createElement('div');
    this.sliver.className = 'ic-sliver';
    this.ignition.appendChild(this.sliver);
    this.buildScene();
    this.buildNames();
    this.buildDust();
    this.buildTitle();
    this.appendLayer('ic-film');
    this.buildCarvedLabel();
    this.letterbox = document.createElement('div');
    this.letterbox.className = 'ic-letterbox';
    this.root.appendChild(this.letterbox);
  }

  private appendLayer(className: string): HTMLDivElement {
    const layer = document.createElement('div');
    layer.className = `ic-layer ${className}`;
    this.stage.appendChild(layer);
    return layer;
  }

  private buildScene(): void {
    // The real CenotaphRenderer canvas occupies the former faked-sphere layer, so
    // the DOM typography + letterbox still composite on top exactly as proven.
    const layer = this.appendLayer('ic-marble');
    this.engineScene.element.classList.add('ic-marble-canvas', 'ic-scene-canvas');
    layer.appendChild(this.engineScene.element);
  }

  private buildNames(): void {
    this.namesGroup = this.appendLayer('ic-names');
    const grid = document.createElement('div');
    grid.className = 'ic-names-grid';
    this.appendNameTiles(grid);
    this.sweep = document.createElement('div');
    this.sweep.className = 'ic-names-sweep';
    const ground = document.createElement('div');
    ground.className = 'ic-names-ground';
    this.namesGroup.append(grid, ground, this.sweep);
  }

  private appendNameTiles(grid: HTMLElement): void {
    const cloud = introPointCloud(NAME_SEED, NAME_POOL.length);
    NAME_POOL.forEach((name, i) => {
      const p = cloud[i]!;
      const el = document.createElement('div');
      el.className = 'ic-name';
      el.textContent = name;
      el.style.left = `${(6 + p.x * 88).toFixed(2)}%`;
      el.style.top = `${(8 + p.y * 84).toFixed(2)}%`;
      el.style.setProperty('--ic-name-z', `${(-260 + p.z * 320).toFixed(0)}px`);
      el.style.opacity = (0.24 + p.z * 0.5).toFixed(2);
      grid.appendChild(el);
    });
  }

  private buildDust(): void {
    this.dust = this.appendLayer('ic-dust');
    for (const p of introPointCloud(MOTE_SEED, MOTE_COUNT)) {
      const el = document.createElement('span');
      el.className = 'ic-mote';
      el.style.setProperty('--ic-mote-size', `${(1 + p.z * 2.5).toFixed(2)}px`);
      this.dust.appendChild(el);
      this.motes.push({ el, x: p.x, y: p.y, z: p.z });
    }
  }

  private buildTitle(): void {
    this.titleGroup = this.appendLayer('ic-title');
    this.titleGroup.appendChild(
      createPlateLayer({ className: 'ic-title-plate', src: '/assets/ui/plate-title.png' }),
    );
    this.titleMark = document.createElement('div');
    this.titleMark.className = 'ic-title-mark';
    this.titleMark.setAttribute('aria-label', TITLE_TEXT);
    this.populateGlyphs();
    // Reference title rake — the cold light band that sweeps the carved mark.
    this.titleRake = document.createElement('div');
    this.titleRake.className = 'ic-title-rake';
    this.titleRake.style.opacity = '0';
    this.titleMark.appendChild(this.titleRake);
    this.subLine = document.createElement('div');
    this.subLine.className = 'ic-title-sub';
    this.enter = document.createElement('div');
    this.enter.className = 'ic-enter';
    this.enter.textContent = ENTER_TEXT;
    this.enter.style.opacity = '0';
    this.titleGroup.append(this.titleMark, this.subLine, this.enter);
  }

  private populateGlyphs(): void {
    for (const char of TITLE_TEXT) {
      const span = document.createElement('span');
      if (char === ' ') {
        span.className = 'ic-glyph-gap';
        span.setAttribute('aria-hidden', 'true');
        span.textContent = ' ';
      } else {
        span.className = 'ic-glyph';
        span.textContent = char;
        span.style.opacity = '0';
        this.glyphs.push(span);
      }
      this.titleMark.appendChild(span);
    }
  }

  private buildCarvedLabel(): void {
    this.carvedLabel = document.createElement('div');
    this.carvedLabel.className = 'ic-carved-label';
    this.carvedLabel.textContent = formatNamesCarved();
    this.carvedLabel.style.opacity = '0';
    this.stage.appendChild(this.carvedLabel);
  }

  private populateStars(): void {
    for (const p of introPointCloud(STAR_SEED, STAR_COUNT)) {
      const star = document.createElement('span');
      star.className = 'ic-star';
      star.style.left = `${(p.x * 100).toFixed(2)}%`;
      star.style.top = `${(p.y * 100).toFixed(2)}%`;
      star.style.setProperty('--ic-star-size', `${(1 + p.z * 1.5).toFixed(2)}px`);
      this.starfield.appendChild(star);
      this.stars.push({ el: star, base: 0.35 + p.y * 0.6, phase: p.z, speed: 0.8 + p.x * 2.4 });
    }
  }
}
